import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { Spinner } from "@shopify/polaris";
import { AppDispatch } from "configureStore";
import {
  fetchMedicalCheckQueue,
  MedicalCheck,
} from "services/medical-check.service";
import MedicalCheckBlock from "components/medical-check-block/medical-check-block";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";

interface MedicalCheckQueueProps {
  companyId: string;
  onCountChange?: (count: number) => void;
}

export default function MedicalCheckQueue({
  companyId,
  onCountChange,
}: MedicalCheckQueueProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState<MedicalCheck[]>([]);
  const [approvedItems, setApprovedItems] = useState<MedicalCheck[]>([]);
  const [canUploadMedical, setCanUploadMedical] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMedicalCheckQueue(companyId);
      setPendingItems(data.pendingItems || []);
      setApprovedItems(data.approvedItems || data.approvedByMeItems || []);
      setCanUploadMedical(!!data.canUploadMedical);
      onCountChange?.(data.count || 0);
    } catch {
      toast.error(t("error"));
      setPendingItems([]);
      setApprovedItems([]);
      setCanUploadMedical(false);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, onCountChange, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleViewFile = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
    dispatch(setIsModalxOpen(true));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="large" />
      </div>
    );
  }

  const hasItems = pendingItems.length > 0 || approvedItems.length > 0;

  if (!hasItems) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm mt-4">
        <p className="text-sm text-gray-600">{t("no_medical_checks_company")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {pendingItems.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900">
            {t("medical_checks_pending")}
          </h2>
          {pendingItems.map((check) => (
            <MedicalCheckBlock
              key={check.id}
              companyId={companyId}
              check={check}
              canApproveMedical={check.canApproveMedical}
              canUpload={canUploadMedical}
              medicalApprovers={check.medicalApprovers}
              onReload={loadData}
              onViewFile={handleViewFile}
              showEmployeeName
            />
          ))}
        </section>
      )}

      {approvedItems.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900">
            {t("medical_checks_approved")}
          </h2>
          {approvedItems.map((check) => (
            <MedicalCheckBlock
              key={check.id}
              companyId={companyId}
              check={check}
              canApproveMedical={false}
              canUpload={false}
              medicalApprovers={check.medicalApprovers}
              onReload={loadData}
              onViewFile={handleViewFile}
              showEmployeeName
              completedView
            />
          ))}
        </section>
      )}
    </div>
  );
}
